/**
 * paire-app#128 — the allergens the restaurant profile renders as badges.
 *
 * Data source: these are `KeywordOption` rows in the `dietary_accommodations`
 * group, seeded by paire-api migration
 * `0041_update_keyword_options_master_list.py` (lines 84-92). The `key`
 * values below are copied from that migration verbatim, and the array order
 * is the migration's order (which is also the order of Josh's reference
 * design on paire-app#94).
 *
 * Outlets select them on the PRO dashboard (paire-web
 * `DiscoveryKeywordsSection` → "Dietary Accommodations"). The selection is
 * stored as `outlet_keywords` rows and reaches the member app as plain keys in
 * `restaurant.keywords` on `GET /restaurants/{id}/summary`. The backend only
 * sends them for verified outlets with an active subscription.
 *
 * If paire-api ever seeds a new `*_allergy` key, it will not appear here. It
 * will still show up as a text chip under About → "Dietary Accommodations",
 * because `RestaurantAboutSection` only hides the keys listed in this file.
 */
import {
  Bean,
  Egg,
  Fish,
  Milk,
  Nut,
  Shrimp,
  Wheat,
  createLucideIcon,
  type LucideIcon,
} from 'lucide-react';
import { allergenLabels } from '@/copy';

/**
 * Peanut — lucide 0.546 has no peanut icon (`Nut` is the tree-nut shape).
 * A two-lobed shell drawn on lucide's 24×24 grid, tilted like a real peanut.
 */
const Peanut = createLucideIcon('peanut', [
  [
    'path',
    {
      d: 'M12 3a4.5 4.5 0 0 0-4.5 4.5c0 1.6.8 2.7 1.4 3.5.3.4.3.9 0 1.2-.6.8-1.4 1.9-1.4 3.5a4.5 4.5 0 0 0 9 0c0-1.6-.8-2.7-1.4-3.5-.3-.4-.3-.9 0-1.2.6-.8 1.4-1.9 1.4-3.5A4.5 4.5 0 0 0 12 3Z',
      transform: 'rotate(40 12 12)',
      key: 'peanut-shell',
    },
  ],
  ['path', { d: 'M10.5 6.5h.01', transform: 'rotate(40 12 12)', key: 'peanut-dot-1' }],
  ['path', { d: 'M13.5 8.5h.01', transform: 'rotate(40 12 12)', key: 'peanut-dot-2' }],
  ['path', { d: 'M10.5 15.5h.01', transform: 'rotate(40 12 12)', key: 'peanut-dot-3' }],
  ['path', { d: 'M13.5 17.5h.01', transform: 'rotate(40 12 12)', key: 'peanut-dot-4' }],
]);

/** One teardrop seed centred on the origin; placed by each node's transform. */
const SEED_PATH = 'M0-3.4C1.8-2 2 1.1 0 2.8-2 1.1-1.8-2 0-3.4Z';

/** Sesame — lucide 0.546 has no sesame icon. Five filled seeds, scattered. */
const Sesame = createLucideIcon('sesame', [
  ['path', { d: SEED_PATH, transform: 'translate(6.5 6.5) rotate(-35)', fill: 'currentColor', key: 'seed-1' }],
  ['path', { d: SEED_PATH, transform: 'translate(17 5.5) rotate(25)', fill: 'currentColor', key: 'seed-2' }],
  ['path', { d: SEED_PATH, transform: 'translate(12 12) rotate(-5)', fill: 'currentColor', key: 'seed-3' }],
  ['path', { d: SEED_PATH, transform: 'translate(5.5 17.5) rotate(20)', fill: 'currentColor', key: 'seed-4' }],
  ['path', { d: SEED_PATH, transform: 'translate(17.5 18) rotate(-40)', fill: 'currentColor', key: 'seed-5' }],
]);

export interface AllergyAccommodationOption {
  /** `KeywordOption.key` in paire-api (e.g. `shellfish_allergy`). */
  key: string;
  /** Short badge label (from the copy deck). */
  label: string;
  Icon: LucideIcon;
}

export const ALLERGY_ACCOMMODATION_OPTIONS: readonly AllergyAccommodationOption[] = [
  { key: 'shellfish_allergy', label: allergenLabels.shellfish, Icon: Shrimp },
  { key: 'seafood_allergy', label: allergenLabels.seafood, Icon: Fish },
  { key: 'wheat_allergy', label: allergenLabels.wheat, Icon: Wheat },
  { key: 'sesame_allergy', label: allergenLabels.sesame, Icon: Sesame },
  { key: 'peanut_allergy', label: allergenLabels.peanut, Icon: Peanut },
  { key: 'tree_nut_allergy', label: allergenLabels.treeNut, Icon: Nut },
  { key: 'egg_allergy', label: allergenLabels.egg, Icon: Egg },
  { key: 'dairy_allergy', label: allergenLabels.dairy, Icon: Milk },
  { key: 'soy_allergy', label: allergenLabels.soy, Icon: Bean },
];

/** The keys above as a set, so other sections can skip them and avoid showing them twice. */
export const ALLERGY_ACCOMMODATION_KEYS: ReadonlySet<string> = new Set(
  ALLERGY_ACCOMMODATION_OPTIONS.map((option) => option.key),
);
